import { Input } from '@tempo-io/sui-input';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseDialog } from '@/common/components/base-dialog/BaseDialog';
import { validateFieldValue } from './validation';

interface LinkEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialValue?: { url: string; description?: string };
  onSave: (value: { url: string; description?: string }) => void;
  onClear?: () => void;
  mode: 'add' | 'edit';
}

export const LinkEditDialog = ({
  open,
  onOpenChange,
  initialValue,
  onSave,
  onClear,
  mode,
}: LinkEditDialogProps) => {
  const { t } = useTranslation();
  const [urlValue, setUrlValue] = useState(initialValue?.url || (mode === 'add' ? 'https://' : ''));
  const [descriptionValue, setDescriptionValue] = useState(initialValue?.description || '');
  const [validationError, setValidationError] = useState<string>();

  useEffect(() => {
    if (open) {
      setUrlValue(initialValue?.url || (mode === 'add' ? 'https://' : ''));
      setDescriptionValue(initialValue?.description || '');
      setValidationError(undefined);
    }
  }, [open, initialValue, mode]);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setValidationError(undefined);
    }
    onOpenChange(newOpen);
  };

  const handleSave = () => {
    if (!urlValue.trim()) {
      onOpenChange(false);
      return;
    }

    const validationResult = validateFieldValue(t, 'LINK', {
      url: urlValue,
      description: descriptionValue || undefined,
    });

    if (!validationResult.isValid) {
      setValidationError(validationResult.error);
      return;
    }

    onSave({
      url: urlValue,
      description: descriptionValue || undefined,
    });
    onOpenChange(false);
  };

  return (
    <BaseDialog
      open={open}
      onOpenChange={handleOpenChange}
      showCloseButton
      className="z-1400!"
      title={
        mode === 'add'
          ? t(($) => $.adaptivePlanner.itemPanel.linkField.dialog.addTitle)
          : t(($) => $.adaptivePlanner.itemPanel.linkField.dialog.editTitle)
      }
      primaryAction={{
        label: t(($) => $.common.save),
        onClick: handleSave,
        'data-testid': 'link-save-button',
      }}
      secondaryAction={{
        label: t(($) => $.common.cancel),
        onClick: () => onOpenChange(false),
        'data-testid': 'link-cancel-button',
      }}
      additionalAction={
        onClear && mode === 'edit'
          ? {
              label: t(($) => $.adaptivePlanner.itemPanel.linkField.clear),
              onClick: () => {
                onClear();
                onOpenChange(false);
              },
              'data-testid': 'link-clear-button',
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-4 py-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="link-url" className="text-sm font-medium">
            {t(($) => $.adaptivePlanner.itemPanel.linkField.urlLabel)}
          </label>
          <Input
            id="link-url"
            data-testid="link-url-input"
            type="text"
            value={urlValue}
            onChange={(e) => setUrlValue(e.target.value)}
            placeholder={t(($) => $.adaptivePlanner.itemPanel.linkField.urlPlaceholder)}
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="link-description" className="text-sm font-medium">
            {t(($) => $.adaptivePlanner.itemPanel.linkField.descriptionLabel)}
          </label>
          <Input
            id="link-description"
            data-testid="link-description-input"
            type="text"
            value={descriptionValue}
            onChange={(e) => setDescriptionValue(e.target.value)}
            placeholder={t(($) => $.adaptivePlanner.itemPanel.linkField.descriptionPlaceholder)}
          />
        </div>

        {validationError && (
          <span className="text-sm text-text-error" data-testid="link-validation-error">
            {validationError}
          </span>
        )}
      </div>
    </BaseDialog>
  );
};
